// src/app/views/landing/Landing.jsx
import React from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Grid,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { getPathByCdExt } from "app/utils/utils";
import { EXAM_TYPES } from "app/types/examTypes";

// ─── Design tokens (light mode — consistent with app-wide white background) ───
const T = {
  surface:      '#ffffff',
  surfaceHover: '#f4f7fd',
  border:       '#e2e8f0',
  borderHover:  '#c5d2e8',
  gold:         '#9a6e00',
  goldFaint:    'rgba(154, 110, 0, 0.08)',
  text:         '#1a2535',
  textMuted:    '#546e8a',
  textDim:      '#94a8be',
};

// ─── Exam metadata ─────────────────────────────────────────────────────────────
// Note: subtitles are editorial additions — verify Korean phrasing before production.
const EXAM_META = {
  g5:  { accent: '#1565c0', light: 'rgba(21, 101, 192, 0.06)',  title: '5급 공채' },
  leet:{ accent: '#1b5e20', light: 'rgba(27, 94, 32, 0.06)',    title: '법학적성시험' },
  g7:  { accent: '#6a1b9a', light: 'rgba(106, 27, 154, 0.06)',  title: '7급 공채' },
  g72: { accent: '#bf360c', light: 'rgba(191, 54, 12, 0.06)',   title: '7급 2차 시험' },
  bar: { accent: '#006064', light: 'rgba(0, 96, 100, 0.06)',    title: '변호사 시험' },
};

// ─── Styled components ─────────────────────────────────────────────────────────

const HeroBanner = styled(Box)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  paddingTop: theme.spacing(3),
  paddingBottom: theme.spacing(2.5),
  background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
}));

const ExamCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'examkey',
})(({ examkey }) => {
  const { accent, light } = EXAM_META[examkey] ?? { accent: T.gold, light: T.goldFaint };
  return {
    position: 'relative',
    background: T.surface,
    border: `1px solid ${T.border}`,
    borderLeft: `3px solid ${accent}`,
    borderRadius: '6px',
    padding: '12px 14px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
    transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.15s ease',
    '&:hover': {
      background: light,
      borderColor: T.borderHover,
      borderLeft: `3px solid ${accent}`,
      transform: 'translateY(-2px)',
      boxShadow: `0 6px 20px rgba(0,0,0,0.1), 0 0 0 1px ${accent}20`,
    },
  };
});

const ActionButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'examkey' && prop !== 'btntype',
})(({ examkey, btntype }) => {
  const { accent, light } = EXAM_META[examkey] ?? { accent: T.gold, light: T.goldFaint };
  const isPrimary = btntype === 'primary';
  return {
    width: '100%',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 12px',
    borderRadius: '4px',
    fontSize: '13px',
    fontWeight: isPrimary ? 600 : 500,
    letterSpacing: '0.3px',
    textTransform: 'none',
    marginBottom: '8px',
    ...(isPrimary
      ? {
          backgroundColor: light,
          color: accent,
          border: `1px solid ${accent}30`,
          '&:hover': { backgroundColor: `${accent}12`, borderColor: `${accent}55` },
        }
      : {
          backgroundColor: T.goldFaint,
          color: T.gold,
          border: `1px solid ${T.gold}30`,
          '&:hover': { backgroundColor: 'rgba(154, 110, 0, 0.14)', borderColor: `${T.gold}55` },
        }),
  };
});

const ExternalLink = styled(Button)({
  color: T.textMuted,
  border: `1px solid ${T.border}`,
  borderRadius: '4px',
  fontSize: '12px',
  padding: '7px 20px',
  letterSpacing: '0.3px',
  textTransform: 'none',
  '&:hover': {
    color: T.text,
    borderColor: T.borderHover,
    backgroundColor: T.surfaceHover,
  },
});

// ─── Sub-components ────────────────────────────────────────────────────────────

// Rendered on the blue banner — white/translucent pills
function ExamBadge({ label }) {
  return (
    <Box sx={{ px: 1.5, py: 0.5, border: '1px solid rgba(255,255,255,0.25)', borderRadius: '3px', bgcolor: 'rgba(255,255,255,0.12)' }}>
      <Typography sx={{ fontSize: '10px', color: 'rgba(255,255,255,0.82)', letterSpacing: '0.5px', lineHeight: 1.4 }}>
        {label}
      </Typography>
    </Box>
  );
}

function SectionDivider({ label }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1.5 }}>
      <Box sx={{ width: 3, height: 16, bgcolor: T.gold, borderRadius: '1px', flexShrink: 0 }} />
      <Typography sx={{ fontSize: '10px', letterSpacing: '3px', color: T.textMuted, fontWeight: 600, textTransform: 'uppercase' }}>
        {label}
      </Typography>
      <Box sx={{ flex: 1, height: '1px', bgcolor: T.border }} />
    </Box>
  );
}

function CardHeader({ examkey }) {
  const meta = EXAM_META[examkey];
  return (
    <Typography sx={{ fontSize: '18px', fontWeight: 700, color: T.text, lineHeight: 1.3, mb: 1.5 }}>
      {meta.title}
    </Typography>
  );
}

function Arrow() {
  return <span style={{ fontSize: '11px', opacity: 0.55 }}>→</span>;
}

function Diamond() {
  return <span style={{ fontSize: '8px', opacity: 0.65 }}>◆</span>;
}

// ─── Main component ────────────────────────────────────────────────────────────

export default function Landing() {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#fff' }}>
      {/* ── Hero ──────────────────────────────────────────────────────────────── */}
      <HeroBanner>
        {/* Decorative circles — G5Land.jsx pattern, with radial gradations */}
        <Box sx={{
          position: 'absolute', bottom: -120, right: -180,
          width: 480, height: 480, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.04) 55%, transparent 100%)',
          pointerEvents: 'none',
        }} />
        <Box sx={{
          position: 'absolute', bottom: -24, right: 32,
          width: 120, height: 120, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.06) 60%, transparent 100%)',
          pointerEvents: 'none',
        }} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '11px', letterSpacing: '4px', color: 'rgba(255,255,255,0.6)', fontWeight: 600, textTransform: 'uppercase', mb: 1 }}>
            Education Center
          </Typography>

          <Box sx={{ width: 48, height: 2, bgcolor: 'rgba(255,255,255,0.5)', mx: 'auto', mb: 1.5 }} />

          <Typography sx={{ fontSize: { xs: '22px', sm: '32px', md: '42px' }, fontWeight: 700, color: '#ffffff', lineHeight: 1.2, mb: 1 }}>
            법률저널 시험 센터
          </Typography>

          <Typography sx={{ fontSize: { xs: '12px', sm: '13px' }, color: 'rgba(255,255,255,0.75)', mb: 0.5 }}>
            오리지널의 힘, 법률저널 전국모의고사
          </Typography>
          <Typography sx={{ fontSize: { xs: '11px', sm: '12px' }, color: 'rgba(255,255,255,0.5)', mb: 2 }}>
            가장 실전답게, 차원이 다른 전략
          </Typography>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
            <ExamBadge label="LEET" />
            <ExamBadge label="5급 공채" />
            <ExamBadge label="외교관후보자" />
            <ExamBadge label="입법고시" />
            <ExamBadge label="법원행시" />
            <ExamBadge label="7급 공채" />
            <ExamBadge label="지역인재 7급" />
            <ExamBadge label="7급 2차" />
            <ExamBadge label="변호사 시험" />
          </Box>
        </Container>
      </HeroBanner>

      {/* ── Exam cards ────────────────────────────────────────────────────────── */}
      <Container maxWidth="lg" sx={{ pt: 2, pb: 2 }}>
        <SectionDivider label="시험 선택" />

        <Grid container spacing={2}>

          {/* 5급 */}
          <Grid item xs={12} sm={6}>
            <ExamCard examkey="g5">
              <CardHeader examkey="g5" />
              <Box sx={{ mt: 'auto' }}>
                <ActionButton examkey="g5" btntype="primary"
                  onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.G5.rid_cd_ext)}/land/${EXAM_TYPES.G5.rid_cd_ext}/${EXAM_TYPES.G5.rid_imp_sr}`)}>
                  <span>5급 PSAT 시험</span>
                  <Arrow />
                </ActionButton>
                {process.env.NODE_ENV === '-' ? (
                  <ActionButton examkey="g5" btntype="prediction" onClick={() => { alert('준비 중입니다.'); }}>
                    <span>5급 예측시스템</span>
                    <Diamond />
                  </ActionButton>
                ) : (
                  <ActionButton examkey="g5" btntype="prediction"
                    onClick={() => { navigate(`/pre/${EXAM_TYPES.G5.rid_cd_ext}/verify?ext=${EXAM_TYPES.G5.rid_cd_ext}&yr=${currentYear}`); }}>
                    <span>5급 예측시스템</span>
                    <Diamond />
                  </ActionButton>
                )}
              </Box>
            </ExamCard>
          </Grid>

          {/* LEET */}
          <Grid item xs={12} sm={6}>
            <ExamCard examkey="leet">
              <CardHeader examkey="leet" />
              <Box sx={{ mt: 'auto' }}>
                <ActionButton examkey="leet" btntype="primary"
                  onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.LEET.rid_cd_ext)}/land/${EXAM_TYPES.LEET.rid_cd_ext}/${EXAM_TYPES.LEET.rid_imp_sr}`)}>
                  <span>LEET 시험</span>
                  <Arrow />
                </ActionButton>
                {process.env.NODE_ENV === '-' ? (
                  <ActionButton examkey="leet" btntype="prediction" onClick={() => { alert('준비 중입니다.'); }}>
                    <span>LEET 예측시스템</span>
                    <Diamond />
                  </ActionButton>
                ) : (
                  <ActionButton examkey="leet" btntype="prediction"
                    onClick={() => { navigate(`/pre/${EXAM_TYPES.LEET.rid_cd_ext}/verify?ext=${EXAM_TYPES.LEET.rid_cd_ext}&yr=${currentYear}`); }}>
                    <span>LEET 예측시스템</span>
                    <Diamond />
                  </ActionButton>
                )}
              </Box>
            </ExamCard>
          </Grid>

          {/* 7급 */}
          <Grid item xs={12} sm={6}>
            <ExamCard examkey="g7">
              <CardHeader examkey="g7" />
              <Box sx={{ mt: 'auto' }}>
                <ActionButton examkey="g7" btntype="primary"
                  onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.G7.rid_cd_ext)}/land/${EXAM_TYPES.G7.rid_cd_ext}/${EXAM_TYPES.G7.rid_imp_sr}`)}>
                  <span>7급 PSAT 시험</span>
                  <Arrow />
                </ActionButton>
                {process.env.NODE_ENV === '-' ? (
                  <ActionButton examkey="g7" btntype="prediction" onClick={() => { alert('준비 중입니다.'); }}>
                    <span>7급 예측시스템</span>
                    <Diamond />
                  </ActionButton>
                ) : (
                  <ActionButton examkey="g7" btntype="prediction"
                    onClick={() => { navigate(`/pre/${EXAM_TYPES.G7.rid_cd_ext}/verify?ext=${EXAM_TYPES.G7.rid_cd_ext}&yr=${currentYear}`); }}>
                    <span>7급 예측시스템</span>
                    <Diamond />
                  </ActionButton>
                )}
              </Box>
            </ExamCard>
          </Grid>

          {/* 7급 2차 */}
          <Grid item xs={12} sm={6}>
            <ExamCard examkey="g72">
              <CardHeader examkey="g72" />
              <Box>
                {process.env.NODE_ENV === '-' ? (
                  <ActionButton examkey="g72" btntype="primary" onClick={() => { alert('준비 중입니다.'); }}>
                    <span>7급 2차 시험</span>
                    <Arrow />
                  </ActionButton>
                ) : (
                  <ActionButton examkey="g72" btntype="primary"
                    onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.G72.rid_cd_ext)}/land/${EXAM_TYPES.G72.rid_cd_ext}/${EXAM_TYPES.G72.rid_imp_sr}`)}>
                    <span>7급 2차 시험</span>
                    <Arrow />
                  </ActionButton>
                )}
              </Box>
            </ExamCard>
          </Grid>

          {/* 변호사 시험 */}
          <Grid item xs={12} sm={6}>
            <ExamCard examkey="bar">
              <CardHeader examkey="bar" />
              <Box>
                {process.env.NODE_ENV === '-' ? (
                  <ActionButton examkey="bar" btntype="primary" onClick={() => { alert('준비 중입니다.'); }}>
                    <span>변호사 시험</span>
                    <Arrow />
                  </ActionButton>
                ) : (
                  <ActionButton examkey="bar" btntype="primary"
                    onClick={() => navigate(`/pub/${getPathByCdExt(EXAM_TYPES.BAR.rid_cd_ext)}/land/${EXAM_TYPES.BAR.rid_cd_ext}/${EXAM_TYPES.BAR.rid_imp_sr}`)}>
                    <span>변호사 시험</span>
                    <Arrow />
                  </ActionButton>
                )}
              </Box>
            </ExamCard>
          </Grid>

        </Grid>

        {/* ── External links ────────────────────────────────────────────────── */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 2.5, mb: 1.5 }}>
          <Box sx={{ flex: 1, height: '1px', bgcolor: T.border }} />
          <Typography sx={{ fontSize: '10px', letterSpacing: '3px', color: T.textDim, textTransform: 'uppercase', flexShrink: 0 }}>
            커뮤니티 & 홈페이지
          </Typography>
          <Box sx={{ flex: 1, height: '1px', bgcolor: T.border }} />
        </Box>

        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'center', justifyContent: 'center', gap: 1.5, pb: 4 }}>
          <ExternalLink variant="outlined" onClick={() => window.open('https://cafe.naver.com/lecpsat', '_blank')}>
            PSAT 카페
          </ExternalLink>
          <ExternalLink variant="outlined" onClick={() => window.open('https://cafe.naver.com/lecleet', '_blank')}>
            LEET 카페
          </ExternalLink>
          <ExternalLink variant="outlined" onClick={() => window.open('http://www.lec.co.kr', '_blank')}>
            법률저널 홈페이지
          </ExternalLink>
        </Box>
      </Container>
    </Box>
  );
}
